import { Button } from '@creditornot/cb-button';
import { InputCheckbox } from '@creditornot/cb-checkbox';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input, Textarea } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import { last } from 'ramda';
import React, { useCallback, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { APIErrorNotification, RFFFieldErrorWrapper, WoltModal } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';
import ImageUploadInput, { ImageValue } from 'modules/menu/components/ImageUploadInput';
import { useCreateCategoriesTracking } from 'modules/menu/hooks/telemetryHooks';
import { createCategory } from 'modules/shared-menu/api';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { SharedMenu } from 'modules/shared-menu/types';

import { useSelectionData } from '../../../hooks/useSelectionData';
import { useProcessSelectionImage } from '../hooks/useProcessSelectionImage';
import { fromAddCategoryFormValuesToApi } from '../transformers';
import { CreateCategoryPayload } from '../types';
import { categoryValidationSchema } from '../validationSchemas';

type Props = {
  className?: string;
  isOpen: boolean;
  onClose: () => void;
};

type CreateCategoryFormData = {
  name: string | undefined;
  description: string | undefined;
  image: ImageValue;
  isWoltSpecial: boolean;
};

const MAX_NAME_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 1000;

const Root = styled(WoltModal)`
  width: 700px;
  background: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
`;

const StyledImageUploadInput = styled(ImageUploadInput)`
  width: 100%;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
`;

const Buttons = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
`;

const ConfirmButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;

const AddCategoryModal = ({ className, isOpen, onClose }: Props) => {
  const { selection, updateSelectionData, categories } = useSelectionData();
  const trackCreateCategories = useCreateCategoriesTracking();
  const menuPermissions = useOfferingMenuPermissionsContext();
  const menuFeatures = useOfferingMenuFeaturesContext();
  const { menuEditorPage } = useListingManagerRoutes();

  const intl = useIntl();
  const history = useHistory();
  const processSelectionImage = useProcessSelectionImage();

  const anotherCategoryIsAlreadyWoltSpecials = useMemo(() => categories.some(c => c.is_wolt_special), [categories]);

  const {
    mutateAsync: executeUpdateCategory,
    error,
    reset: resetMutation,
  } = useMutation<SharedMenu, Error, CreateCategoryPayload>(async payload => createCategory(selection.id, payload), {
    onSuccess: updatedSelection => {
      updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
      history.push(`${menuEditorPage()}/${last(updatedSelection.categories).id}`);
      handleClose();
    },
  });

  const handleClose = useCallback(() => {
    resetMutation();
    onClose();
  }, [onClose, resetMutation]);

  const initialValues: CreateCategoryFormData = useMemo(
    () => ({
      name: undefined,
      description: undefined,
      image: { type: 'no-image' },
      isWoltSpecial: false,
    }),
    [],
  );

  return (
    <Root className={className} isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="shared-menu.editor.categories.add" />
      </ModalHeader>
      <Form<CreateCategoryFormData>
        onSubmit={async values => {
          const validValues = categoryValidationSchema.validateSync(values);
          const imageUrl = await processSelectionImage(values.image);
          const payload = fromAddCategoryFormValuesToApi(validValues, selection.primary_language, imageUrl);

          try {
            await executeUpdateCategory(payload);
            trackCreateCategories([payload?.name[0].value ?? ''], false);
          } catch (e) {
            // noop, just preventing the throw from leaking
            // error itself is handled & exposed by the useMutation
          }
        }}
        initialValues={initialValues}
        validate={validateYupSchema(categoryValidationSchema)}
      >
        {({ handleSubmit, hasValidationErrors, pristine, submitting }) => (
          <form onSubmit={handleSubmit}>
            <ModalContent style={{ padding: 0 }}>
              <FormRow label={intl.formatMessage({ id: 'menu.categoryName' })} horizontalPadding required>
                <Field<string> name="name">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <InputContainer characterCount={{ value: input.value.length, max: MAX_NAME_LENGTH }}>
                          <Input {...input} maxLength={MAX_NAME_LENGTH} />
                        </InputContainer>
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
              <FormRow label={intl.formatMessage({ id: 'menu.categoryDescription' })} horizontalPadding>
                <Field<string> name="description">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <InputContainer characterCount={{ value: input.value.length, max: MAX_DESCRIPTION_LENGTH }}>
                          <Textarea {...input} maxLength={MAX_DESCRIPTION_LENGTH} />
                        </InputContainer>
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
              {menuPermissions.categoryImages && menuFeatures.categoryImages && (
                <FormRow label={intl.formatMessage({ id: 'menu.categoryImage' })} horizontalPadding>
                  <Field<CreateCategoryFormData['image']> name="image">
                    {({ input }) => (
                      <InputContainer>
                        <StyledImageUploadInput {...input} />
                      </InputContainer>
                    )}
                  </Field>
                </FormRow>
              )}
              <FormRow label="Set as Wolt Specials" horizontalPadding>
                <InputContainer
                  hint={
                    anotherCategoryIsAlreadyWoltSpecials
                      ? intl.formatMessage({ id: 'listing-manager.wolt-specials.another-category-is-set' })
                      : undefined
                  }
                >
                  <Field name="isWoltSpecial" type="checkbox">
                    {field => <InputCheckbox {...field.input} disabled={anotherCategoryIsAlreadyWoltSpecials} />}
                  </Field>
                </InputContainer>
              </FormRow>
            </ModalContent>
            <StyledFooter>
              {error && <APIErrorNotification style={{ width: '100%', marginBlockEnd: '16px' }} error={error} />}
              <Buttons>
                <CancelButton onClick={onClose} disabled={submitting}>
                  <FormattedMessage id="state.cancel" />
                </CancelButton>
                <ConfirmButton
                  type="submit"
                  onClick={handleSubmit}
                  disabled={pristine || submitting || hasValidationErrors}
                  loading={submitting}
                >
                  <FormattedMessage id="state.save" />
                </ConfirmButton>
              </Buttons>
            </StyledFooter>
          </form>
        )}
      </Form>
    </Root>
  );
};

export default AddCategoryModal;
